import { LoadingState, Button } from "components/Shared";
import { UserContext } from "context";
import Datasource, { DatasourceResponse } from "lib/datasource";
import _ from "lodash";
import moment from "moment";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { getForm } from "actions/forms";
import { uploadSignedDocument, undoConditionGeneration } from "actions/conditions";

const CreateFormCondition = ({ condition: originalCondition, task, onUpdate, onUpdateMoment }: any) => {
	const dispatch = useDispatch();
	const { currentUser } = useContext(UserContext) as { currentUser: { id?: string; email?: string } };
	const [condition, setCondition] = useState(originalCondition || {});
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [pendingSignerId, setPendingSignerId] = useState<string | null>(null);
	const [uploadingId, setUploadingId] = useState<string | null>(null);
	const [uploadError, setUploadError] = useState<string | null>(null);
	const conditionId = useMemo(() => _.get(condition, "id"), [condition]);
	const formId = useMemo(() => _.get(condition, "form_id"), [condition]);
	const storedForm = useSelector((state: any) =>
		formId ? _.find(state.forms, { id: formId }) : null
	);
	const [, renderTrigger] = useState(moment());
	const [datasource] = useState(new Datasource({ mainModelName: "condition", renderTrigger: renderTrigger }));

	useEffect(() => {
		setCondition(originalCondition);
	}, [originalCondition]);

	useEffect(() => {
		let sub = datasource.responseSubject$.subscribe((response: DatasourceResponse) => {
			if (response.success && !_.isNil(onUpdate)) {
				onUpdate(response.normalizedMainModelResponse);
				onUpdateMoment && onUpdateMoment();
			}
		});
		return () => sub.unsubscribe();
	}, [datasource, onUpdate, onUpdateMoment]);

	useEffect(() => {
		if (!_.isEmpty(conditionId)) datasource.get("v2", `conditions/${conditionId}`, {});
	}, [datasource, conditionId]);

	useEffect(() => {
		if (formId && !storedForm) {
			dispatch(getForm(formId) as any);
		}
	}, [formId, storedForm, dispatch]);

	if (datasource.isLoading) return <LoadingState />;
	if (_.isEmpty(condition)) return null;

	const form = storedForm || _.get(condition, "form");
	const formCode = condition.form_code || _.get(form, "code");
	const formName = condition.form_name || _.get(form, "name");
	const formDescription = condition.form_description || _.get(form, "description") || condition.description;
	const formReady = condition.form_ready !== false; // undefined → assume ready (legacy)
	const hasGenerated = !!condition.generated_document_id;
	// "Original Form" — the initial generated PDF. Stays put for the lifetime of
	// the condition so anyone can pull it up for reference.
	// Per api-contract.md §2.9: use generated_document_id for in-app navigation,
	// NOT generatedUri (the presigned URL). The URL is kept for any future inline
	// preview use but MUST NOT be used as the navigation target.
	const generatedDocumentId = condition.generated_document_id as string | undefined;
	// generatedUri (condition.generated_document_url / revisions[0].signed_uri)
	// is intentionally NOT bound here — navigation must go via generatedDocumentId.
	const originalFormName = (formCode || formName || "form") + ".pdf";
	// "Latest Form" — the most recent signed upload across all signers. The
	// next signer should download THIS, sign on top, and re-upload, building
	// up a cumulative chain of signatures on a single PDF.
	// Per api-contract.md §2.9: use latest_signed_document_id for in-app navigation.
	const latestSignedDocumentId = condition.latest_signed_document_id as string | undefined;
	// latestSignedUri (condition.latest_signed_document_url) is intentionally NOT
	// bound here — navigation must go via latestSignedDocumentId.
	const latestSignedName = (condition.latest_signed_document_name as string | undefined) || "signed.pdf";
	const latestSigner = condition.latest_signed_document_signer as
		| { id?: string; name?: string; firstname?: string; surname?: string; email?: string }
		| undefined;
	const latestSignerName = latestSigner
		? (latestSigner.name ||
			[latestSigner.firstname, latestSigner.surname].filter(Boolean).join(" ") ||
			latestSigner.email ||
			"signer")
		: null;
	const latestSignedAt = condition.latest_signed_document_signed_at as string | undefined;
	const latestSignedAtLabel = latestSignedAt ? moment(latestSignedAt).calendar() : null;
	const children = (condition.children || []) as any[];
	const legacySigners = (condition.signers || []) as any[];
	// Prefer child-condition signers (new model). Fall back to Signer records (legacy).
	const signers = children.length > 0
		? children.map((c: any) => ({
			id: c.id,
			user: c.user || {},
			status: c.status === "complete" ? "signed" : (c.status || "pending"),
			position: c.position,
		}))
		: legacySigners;
	const signedCount = signers.filter((s: any) => s.status === "signed").length;
	const signersConfigured = signers.length > 0;
	// The "configured" signing view (form header + signer rows) shows only while a
	// generated PDF exists. After an Undo the generated PDF is cleared (but the
	// signer records are kept), so this flips false and the condition reverts to
	// the pristine "Create Form" state; regenerating brings the signers back —
	// all of them back at "pending", since an Undo voids signatures collected
	// against the superseded PDF (Condition#reset_signing_round!). The signed
	// files themselves survive in the Documents tab, tagged "form amended".
	const configured = hasGenerated && signersConfigured;

	// generated_by / generated_at are exposed by the backend as flat attributes
	// computed from generated_document.created_by, falling back to condition.created_by.
	const creator =
		condition.generated_by ||
		_.get(condition, "generated_document.created_by") ||
		condition.created_by ||
		null;
	const creatorName = creator
		? creator.name || [creator.firstname, creator.surname].filter(Boolean).join(" ") || creator.email
		: null;
	const createdAt =
		condition.generated_at ||
		_.get(condition, "generated_document.created_at") ||
		condition.created_at;
	const createdAtLabel = createdAt ? moment(createdAt).calendar() : null;

	const allSigned = signers.length > 0 && signedCount === signers.length;
	// "parallel" / "sequential" — surfaced inline in the section heading so
	// users immediately see the rule that governs the signer rows below. The
	// backend value "any" maps to the user-facing label "parallel" to match
	// the wording on the Configure Signers page.
	const orderSuffix =
		(condition.signing_order || "any") === "sequential" ? "sequential" : "parallel";

	// In SEQUENTIAL signing the next signer must download/sign on top of the
	// Latest Form, not the Original — so once the first signature is in we
	// grey out the Original Form to steer people to the right file. It stays
	// fully accessible (the View link still works); this is a visual de-emphasis
	// only. Parallel signing is unaffected: every signer works off the Original.
	const isSequential = (condition.signing_order || "any") === "sequential";
	const dimOriginalForm = isSequential && signedCount > 0;

	// Only the Responsible user or a project administrator may roll a generated
	// form back to the "Create Form" state so it can be amended and re-generated.
	// `can_amend` is computed server-side (ConditionSerializer) from the current
	// user's role on the task; fall back to task flags if the attribute is absent.
	const canUndoGeneration = !!(
		condition.can_amend ?? (task?.is_responsible || task?.is_administrator)
	);
	const undoGeneration = async () => {
		try {
			await dispatch(undoConditionGeneration(conditionId) as any);
			// Refresh so the header returns to "Create Form". The signer set is
			// kept, but the backend resets the whole round to pending and detaches
			// the collected signatures (Condition#reset_signing_round!), so the
			// rows come back clean — no "Replace", no "Latest form".
			datasource.get("v2", `conditions/${conditionId}`, {});
		} catch (e) {
			setUploadError((e as any)?.message || "Failed to undo the form");
		}
	};

	return (
		<div className="condition-create-form">
			{/* Header row — title + minimal actions */}
			<div className="d-flex justify-content-between align-items-start flex-wrap" style={{ gap: 12 }}>
				<div style={{ flex: 1, minWidth: 0 }}>
					{(formCode || formName) && (
						<div className="mb-1">
							<strong className="mr-2">
								{configured
									? `Form ${formCode || formName || ""}${allSigned ? " Signed" : ""}`
									: formName || formCode}
							</strong>
						</div>
					)}
					{!configured && !!formDescription && (
						<p className="mb-2" style={{ color: "#4B5563" }}>{formDescription}</p>
					)}
					{/* "Form generated by …" line removed — the same attribution now
					    lives on the Original form card's subtitle, so showing it here
					    too was redundant. */}
				</div>
				<div className="d-flex align-items-center" style={{ gap: 8 }}>
					{!hasGenerated && formReady && (
						<Link to={`/conditions/${condition.id}/fill`} className="btn btn-primary btn-sm">
							Create Form
						</Link>
					)}
					{!hasGenerated && !formReady && (
						<span className="badge badge-danger" title="The linked form has no .docx uploaded. Re-upload on the property Forms tab.">
							Form file missing
						</span>
					)}
					{/* PDF is already on the condition (generated_document_id is set)
					    but no signers have been configured yet — skip FormFill entirely
					    and go straight to the signer-config page. Re-entering FormFill
					    here would just bootstrap an unnecessary preview render before
					    forcing the user to click "Generate PDF & Set Up Signing" again
					    to reach this same destination. */}
					{hasGenerated && !signersConfigured && (
						<Link to={`/conditions/${condition.id}/signers`} className="btn btn-primary btn-sm">
							Assign signers
						</Link>
					)}
				</div>
			</div>

			{configured && (
				<>
					{/* 1. Original form — the initial generated PDF, view-only reference
					    that never changes for the lifetime of the condition.
					    Navigation target is /documents/<id>#preview per api-contract.md §2.9.
					    Visibility is gated on generated_document_id (the stable UUID), not on
					    generatedUri (the presigned S3 URL which may be absent or expired). */}
					{generatedDocumentId && (
						<div
							className="mt-4"
							style={
								dimOriginalForm
									? { filter: "grayscale(1)", opacity: 0.6 }
									: undefined
							}
							title={
								dimOriginalForm
									? "Superseded by the Latest Form — the next signer should sign on top of that. Still available for reference."
									: undefined
							}
						>
							<div
								className="text-uppercase small mb-2"
								style={{ letterSpacing: "0.05em", color: "#6B7280", fontWeight: 600 }}
							>
								Original form
							</div>
							<div
								className="d-flex align-items-center justify-content-between"
								style={{
									position: "relative",
									padding: "12px 16px",
									border: "1px solid #E5E7EB",
									borderRadius: 8,
								}}
							>
								<div className="d-flex align-items-center" style={{ gap: 12, minWidth: 0, flex: 1 }}>
									<span
										style={{ fontSize: 22, flexShrink: 0, color: "#7C3AED" }}
										aria-hidden="true"
									>
										📄
									</span>
									<div style={{ minWidth: 0, flex: 1 }}>
										<div
											style={{
												fontWeight: 500,
												color: "#111827",
												overflow: "hidden",
												textOverflow: "ellipsis",
												whiteSpace: "nowrap",
											}}
										>
											{originalFormName}
										</div>
										<div
											className="small"
											style={{
												color: "#4B5563",
												overflow: "hidden",
												textOverflow: "ellipsis",
												whiteSpace: "nowrap",
											}}
										>
											Initial generated PDF{creatorName ? <> by <strong style={{ color: "#111827" }}>{creatorName}</strong></> : null}{createdAtLabel ? <span> · {createdAtLabel}</span> : null}
										</div>
									</div>
								</div>
								<div className="d-flex align-items-center" style={{ gap: 12, flexShrink: 0, marginLeft: 12 }}>
									<Link
										className="btn btn-sm"
										style={{
											background: "#38BDF8",
											color: "#fff",
											border: "none",
											padding: "4px 14px",
											fontWeight: 500,
											borderRadius: 6,
											textDecoration: "none",
										}}
										to={`/documents/${generatedDocumentId}#preview`}
									>
										View
									</Link>
									{canUndoGeneration && (
										<span className="condition-toolbar" style={{ position: "absolute", left: "calc(100% + 8px)", top: "50%", transform: "translateY(-50%)", margin: 0 }}>
											<Button kind="undo" confirm small onClick={undoGeneration} />
										</span>
									)}
								</div>
							</div>
						</div>
					)}

					{/* 2. Latest form — the most recent signed upload, which subsequent
					    signers should download and sign on top of. Hidden until the
					    first signer uploads (until then, "Original form" above is the
					    one to sign). */}
					{/* Latest form card — visibility is gated on latest_signed_document_id
					    per api-contract.md §2.9. The presigned latestSignedUri URL is kept
					    in scope for any future inline preview use but navigation uses the id. */}
					{latestSignedDocumentId && (
						<div className="mt-4">
							{/* Heading carries the "next signer" instruction inline so it
							    sits next to the section title, not buried in the row's
							    metadata text. */}
							<div className="d-flex align-items-baseline mb-2" style={{ gap: 10, flexWrap: "wrap" }}>
								<span
									className="text-uppercase small"
									style={{ letterSpacing: "0.05em", color: "#6B7280", fontWeight: 600 }}
								>
									Latest form
								</span>
								<span className="small" style={{ color: "#4B5563" }}>
									(Next signer should download and sign on top)
								</span>
							</div>
							<div
								className="d-flex align-items-center justify-content-between"
								style={{
									padding: "12px 16px",
									border: "1px solid #E5E7EB",
									borderRadius: 8,
								}}
							>
								<div className="d-flex align-items-center" style={{ gap: 12, minWidth: 0, flex: 1 }}>
									<span
										style={{ fontSize: 22, flexShrink: 0, color: "#7C3AED" }}
										aria-hidden="true"
									>
										📄
									</span>
									<div style={{ minWidth: 0, flex: 1 }}>
										<div
											style={{
												fontWeight: 500,
												color: "#111827",
												overflow: "hidden",
												textOverflow: "ellipsis",
												whiteSpace: "nowrap",
											}}
										>
											{latestSignedName}
										</div>
										<div
											className="small"
											style={{
												color: "#4B5563",
												overflow: "hidden",
												textOverflow: "ellipsis",
												whiteSpace: "nowrap",
											}}
										>
											{latestSignerName ? (
												<>
													Last signed by <strong style={{ color: "#111827" }}>{latestSignerName}</strong>
													{latestSignedAtLabel ? <span> • {latestSignedAtLabel}</span> : null}
												</>
											) : (
												<>Latest signed version</>
											)}
										</div>
									</div>
								</div>
								<div className="d-flex align-items-center" style={{ gap: 12, flexShrink: 0, marginLeft: 12 }}>
									<Link
										className="btn btn-sm"
										style={{
											background: "#38BDF8",
											color: "#fff",
											border: "none",
											padding: "4px 14px",
											fontWeight: 500,
											borderRadius: 6,
											textDecoration: "none",
										}}
										to={`/documents/${latestSignedDocumentId}#preview`}
									>
										View
									</Link>
								</div>
							</div>
						</div>
					)}

					{/* 3. Waiting for signatures — each row has inline upload / signed-doc state.
					    Signing order ("any-order" / "sequential") is folded into the heading
					    so the rule sits right next to the rows it governs. */}
					<div className="mt-4">
						{/* Heading + signed-count tally share one row, separated by
						    flex space-between so the count anchors to the right edge
						    of the section above the rows it summarises. */}
						<div className="d-flex align-items-baseline justify-content-between mb-2">
							<div
								className="text-uppercase small"
								style={{ letterSpacing: "0.05em", color: "#6B7280", fontWeight: 600 }}
							>
								Waiting for signatures ({orderSuffix})
							</div>
							<div className="small" style={{ color: "#4B5563" }}>
								{signedCount} of {signers.length} signed
							</div>
						</div>
						<div>
							{(() => {
								// In sequential mode signer i is gated on signer i-1. The
								// "first un-signed" index tells us who's currently next in
								// line; everyone after that is blocked, and each blocked
								// row reports the person directly before them as the one
								// whose signature is missing.
								const isSequential = (condition.signing_order || "any") === "sequential";
								// "Done" for sequencing means COMPLETED (status), not merely
								// having a document — a signer reverted after an upstream
								// Replace keeps their document but is pending again, so they
								// must count as the next unsigned in the chain.
								const firstUnsignedIndex = children.findIndex(
									(c: any) => c.status !== "complete"
								);
								const nameOf = (child: any) => {
									if (!child) return null;
									const bu = child.user || {};
									return bu.name || [bu.firstname, bu.surname].filter(Boolean).join(" ") || bu.email || "the previous signer";
								};
								return children.map((c: any, i: number) => {
								const u = c.user || {};
								const name =
									u.name || [u.firstname, u.surname].filter(Boolean).join(" ") || u.email;
								// hasCompleted drives the "signed" presentation (badge, row
								// colours, sequencing). hasDocument drives the action button:
								// a signer with an uploaded document gets "Replace", otherwise
								// "Upload". A signer reverted by an upstream Replace is
								// hasCompleted=false (Pending) but hasDocument=true (Replace).
								const hasCompleted = c.status === "complete";
								const hasDocument = !!c.signed_document_url;
								const isSigned = hasCompleted;
								const isUploading = uploadingId === c.id;
								// Only the assigned signer themself can upload/replace their
								// signed copy. Backend enforces this via authorize_for_signer!,
								// so even hand-crafted requests fail; here we just hide the
								// affordance for everyone else.
								const isCurrentUserSigner = !!currentUser?.id && u.id === currentUser.id;
								// Sequential: signer i is blocked when the current next-in-
								// line is somewhere before them in the list.
								const isSequentialBlocked =
									isSequential && !isSigned && firstUnsignedIndex !== -1 && i > firstUnsignedIndex;
								// Display the closest *incomplete* predecessor as the blocker.
								// Walking backwards skips earlier signers who are COMPLETE, so
								// the message names whoever this row is actually waiting on in
								// the chain. "Incomplete" is judged by status (not by having a
								// document) so a signer reverted by an upstream Replace — who
								// keeps their document but is pending again — is correctly
								// recognised as the blocker.
								let blockerIdx = -1;
								for (let k = i - 1; k >= 0; k--) {
									const other = children[k];
									if (other?.status !== "complete") {
										blockerIdx = k;
										break;
									}
								}
								const blockerName = isSequentialBlocked && blockerIdx !== -1
									? nameOf(children[blockerIdx])
									: null;
								const rowBg = isSigned ? "#F0FDF4" : "#FFFBEB";
								const rowBorder = isSigned ? "#BBF7D0" : "#FDE68A";
								const numBg = isSigned ? "#16A34A" : "#FCD34D";
								const numColor = isSigned ? "#fff" : "#78350F";
								return (
									/* Outer wrapper takes full row width (matches Original
									   form / Latest form cards above). The Undo button is
									   absolutely positioned to the right so it doesn't shrink
									   the green-tinted row. We wrap it in `condition-toolbar`
									   to inherit the existing grey-default / blue-on-hover
									   styling used by other condition Undo buttons. */
									<div
										key={c.id}
										style={{
											position: "relative",
											marginTop: i === 0 ? 0 : 8,
										}}
									>
									<div
										style={{
											background: rowBg,
											border: `1px solid ${rowBorder}`,
											borderRadius: 8,
											padding: "12px 16px",
											display: "flex",
											alignItems: "center",
											justifyContent: "space-between",
											gap: 12,
										}}
									>
										<div className="d-flex align-items-center" style={{ gap: 12, minWidth: 0 }}>
											<span
												style={{
													display: "inline-flex",
													alignItems: "center",
													justifyContent: "center",
													width: 22,
													height: 22,
													borderRadius: "50%",
													background: numBg,
													color: numColor,
													fontSize: 12,
													fontWeight: 600,
													flexShrink: 0,
												}}
											>
												{c.position != null ? c.position + 1 : i + 1}
											</span>
											<div style={{ minWidth: 0 }}>
												<div style={{ fontWeight: 500, color: "#111827" }}>{name}</div>
												{u.title && (
													<div className="small" style={{ color: "#4B5563" }}>{u.title}</div>
												)}
												{hasDocument && c.signed_document_name && (
													c.signed_document_id ? (
														/* Link to the signer's current signed document. After a
														    Replace, signed_document_id already points at the new
														    (replacement) document, so this always opens the latest. */
														<Link
															to={`/documents/${c.signed_document_id}#preview`}
															className="small"
															style={{ fontStyle: "italic", color: "#2563EB", textDecoration: "underline" }}
														>
															{c.signed_document_name}
														</Link>
													) : (
														<div className="small" style={{ fontStyle: "italic", color: "#4B5563" }}>
															{c.signed_document_name}
														</div>
													)
												)}
												{isSequentialBlocked && blockerName && (
													<div className="small" style={{ fontStyle: "italic", color: "#92400E" }}>
														Awaiting {blockerName}'s signature
													</div>
												)}
											</div>
										</div>
										{/* Status badge + action slot. The action slot has a fixed
										    min-width so the badge's right edge always lands in the
										    same column, whether the row shows a button or not. */}
										<div className="d-flex align-items-center" style={{ gap: 12, flexShrink: 0 }}>
											<span
												style={{
													background: isSigned ? "#DCFCE7" : "#FEF3C7",
													color: isSigned ? "#15803D" : "#92400E",
													fontSize: 12,
													fontWeight: 500,
													padding: "3px 10px",
													borderRadius: 999,
													minWidth: 70,
													textAlign: "center",
												}}
											>
												{isSigned ? "Signed" : "Pending"}
											</span>
											<div
												style={{
													// Widened to fit Replace + Undo side by side on
													// signed rows; pending rows still right-justify
													// their single Upload button into this slot.
													minWidth: 200,
													display: "flex",
													justifyContent: "flex-end",
													alignItems: "center",
													gap: 8,
												}}
											>
												{isCurrentUserSigner && hasDocument && (
													/* Replace swaps the signed PDF in place. Shown whenever
													    the signer has an uploaded document — including after
													    an upstream Replace reverted them to Pending. */
													<button
														className="btn btn-sm"
														style={{
															background: "#38BDF8",
															color: "#fff",
															border: "none",
															padding: "4px 14px",
															fontWeight: 500,
															borderRadius: 6,
															opacity: uploadingId ? 0.7 : 1,
														}}
														onClick={() => {
															setPendingSignerId(c.id);
															fileInputRef.current?.click();
														}}
														disabled={!!uploadingId}
													>
														Replace
													</button>
												)}
												{isCurrentUserSigner && !hasDocument && (
													<button
														className="btn btn-sm"
														style={{
															background: isSequentialBlocked ? "#9CA3AF" : "#38BDF8",
															color: "#fff",
															border: "none",
															padding: "4px 14px",
															fontWeight: 500,
															borderRadius: 6,
															opacity: isUploading ? 0.7 : isSequentialBlocked ? 0.6 : 1,
															cursor: isSequentialBlocked ? "not-allowed" : "pointer",
														}}
														onClick={() => {
															setPendingSignerId(c.id);
															fileInputRef.current?.click();
														}}
														disabled={!!uploadingId || isSequentialBlocked}
														title={isSequentialBlocked ? `Waiting for ${blockerName} to sign first` : undefined}
													>
														{isUploading ? "Uploading…" : "Upload"}
													</button>
												)}
											</div>
										</div>
									</div>
									</div>
								);
								});
							})()}
						</div>
						{uploadError && <div className="alert alert-danger py-2 mt-2">{uploadError}</div>}
					</div>

					{/* Hidden input shared across all signer rows; the row that opened it is tracked via pendingSignerId */}
					<input
						type="file"
						ref={fileInputRef}
						style={{ display: "none" }}
						accept="application/pdf,.pdf"
						onChange={async (e) => {
							const file = e.target.files && e.target.files[0];
							const targetId = pendingSignerId;
							if (!file || !targetId) {
								if (fileInputRef.current) fileInputRef.current.value = "";
								return;
							}
							setUploadingId(targetId);
							setUploadError(null);
							try {
								await dispatch(uploadSignedDocument(targetId, file) as any);
								// Re-fetch the parent so its children list reflects the new signed doc / completion
								datasource.get("v2", `conditions/${conditionId}`, {});
							} catch (err) {
								const ex = err as any;
								const detail =
									ex?.response?.data?.errors?.detail ||
									ex?.response?.data?.errors?.[0]?.detail ||
									ex?.message ||
									"Upload failed";
								setUploadError(Array.isArray(detail) ? detail.join(", ") : detail);
							} finally {
								setUploadingId(null);
								setPendingSignerId(null);
								if (fileInputRef.current) fileInputRef.current.value = "";
							}
						}}
					/>
				</>
			)}
		</div>
	);
};

export default CreateFormCondition;
